import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import { Icon, Popover, Text, useTheme } from '@ui-kitten/components';
import { SafeAreaView } from 'react-native-safe-area-context';
import DatePickerWeb, { useDefaultStyles } from 'react-native-ui-datepicker';
import dayjs from 'dayjs';
import lodashGet from 'lodash/get';

import Button from '../../Components/Button';
import DatePicker from '../../Components/DatePicker';
import ListItem from '../../Components/ListItem';
import GooglePlacesAutocompleteWeb from '../../Components/Map/GooglePlacesAutocomplete';
import Search from '../../Components/Search';
import modals from '../../Components/Sheets/modals';
import constants from '../../Config/constants';
import { pluralize } from '../../Helper';
import { activeAddressSelector } from '../../RTK/user/selectors';
import themeStyle, { componentStyles, globalSpacing } from '../../Theme/styles';
import DateHelper from '../../Helper/Date';
import GoogleAutoSuggestion from '../Address/GoogleAutoSuggestion';

const SearchAddress = constants.isWeb
  ? GooglePlacesAutocompleteWeb
  : GoogleAutoSuggestion;

const BackOrClose = ({ isBack = false, onPress = () => {} }) => {
  const theme = useTheme();
  return (
    <Button
      style={
        isBack ? themeStyle.spacingRightMedium : themeStyle.spacingLeftMedium
      }
      onPress={onPress}
      plain
    >
      <View style={[styles.firstRowRadiusAndBg, styles.button]}>
        <Icon
          name={isBack ? 'arrow-back' : 'close'}
          fill={theme['text-basic-color']}
          style={styles.buttonIcon}
        />
      </View>
    </Button>
  );
};

const StyledSearch = forwardRef(
  ({ placeholder, onBlur, onChangeText, onFocus, onRef }: any, _) => {
    const theme = useTheme();
    return (
      <Search
        ref={onRef}
        fill={theme['text-basic-color']}
        inputStyle={[styles.firstRowRadiusAndBg, themeStyle.flex1]}
        placeholder={placeholder}
        onBlur={onBlur}
        onFocus={onFocus}
        onSearch={(d) => onChangeText(d)}
      />
    );
  },
);

const ReservationLocateOnMapHeader = ({
  onFilterChanged,
  onClose,
  filter,
}: {
  onFilterChanged: (d: { [key: string]: any }) => void;
  onClose: () => void;
  filter: { [key: string]: any };
}) => {
  const inputRef = useRef<{ _updateInputValue: (val: string) => void }>();
  const theme = useTheme();
  const defaultStyles = useDefaultStyles();
  const activeAddress = useSelector(activeAddressSelector);
  const [popoverDate, setPopoverDate] = useState('');
  const [popoverSize, setPopoverSize] = useState(1);
  const [selectedDate, setSelectedDate] = useState(
    dayjs(
      `${filter.date} ${filter.time}`,
      `${constants.DBDATE_FORMAT} ${constants.DBTIME_FORMAT}`,
    ),
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestWeb, setShowGuestWeb] = useState(false);
  const [size, setSize] = useState(1);

  useEffect(() => {
    inputRef.current._updateInputValue(activeAddress.formatted_address);
  }, []);

  const _changePartySize = async () => {
    if (constants.isWeb) {
      setShowGuestWeb(true);
    } else {
      const result = ((await modals.show(modals.PROMPT_WITH_INPUT, {
        title: 'Party Size',
        message: 'Enter party size',
        buttonOkStatus: 'info',
        inputs: [
          {
            name: 'size',
            type: 'number',
            value: size.toString(),
          },
        ],
      })) || {}) as object;
      if ('size' in result) {
        const nsize = Number(result.size);
        setSize(nsize);
        onFilterChanged({ party_size: nsize });
      }
    }
  };

  const _onAddressSelect = (param1, param2) => {
    const loc = lodashGet(param2, 'geometry.location');
    inputRef.current._updateInputValue(param1.description);
    onFilterChanged({ location: `${loc.lat},${loc.lng}` });
  };

  return (
    <View style={styles.wrapper}>
      <SafeAreaView edges={['top']}>
        <View style={[themeStyle.flex1, themeStyle.pageHorizontalSpacing]}>
          <SearchAddress
            // crossplatform props
            renderLeftButton={
              !constants.isWeb
                ? () => <BackOrClose onPress={onClose} isBack />
                : null
            }
            renderRightButton={
              constants.isWeb ? () => <BackOrClose onPress={onClose} /> : null
            }
            inbetweenCompo={
              // Date and party size
              <View style={themeStyle.pageVerticalSpacingMedium}>
                <View style={styles.dateAndPartySizeContainer}>
                  <View style={{ flex: 1.3 }}>
                    {/* on mobile, it uses native date picker */}
                    {/* on web, it popover date picker */}
                    <Popover
                      visible={constants.isWeb && showDatePicker}
                      anchor={() => (
                        <Button onPress={() => setShowDatePicker(true)} plain>
                          <ListItem
                            contentContainerStyle={[
                              themeStyle.pageVerticalSpacingMedium,
                              themeStyle.pageHorizontalSpacingSmall,
                            ]}
                            leftIcon={{ name: 'calendar-outline' }}
                            rightIcon={{ name: 'chevron-down' }}
                            spacing={5}
                            title={
                              <Text numberOfLines={1}>
                                {dayjs(selectedDate).isSame(dayjs(), 'day')
                                  ? 'Today'
                                  : dayjs(selectedDate).format(
                                      'MMM DD, hh:mm A',
                                    )}
                              </Text>
                            }
                          />
                        </Button>
                      )}
                      placement="bottom"
                      onBackdropPress={() => setShowDatePicker(false)}
                      style={themeStyle.spacingTopSmall}
                      fullWidth
                    >
                      <View
                        style={[
                          themeStyle.pageVerticalSpacingSmall,
                          themeStyle.pageHorizontalSpacingSmall,
                          themeStyle.shadow,
                        ]}
                      >
                        <DatePickerWeb
                          date={dayjs(popoverDate ? popoverDate : undefined)}
                          minDate={dayjs()}
                          mode="single"
                          styles={{
                            ...defaultStyles,
                            ...componentStyles.eventDatepicker,
                          }}
                          onChange={({ date }) => {
                            if (date) {
                              setPopoverDate(dayjs(date).toISOString());
                            }
                          }}
                          timePicker
                          use12Hours
                        />
                        <Button
                          size="small"
                          status="info"
                          style={[
                            themeStyle.alignSelfEnd,
                            themeStyle.spacingBottomSmall,
                            { borderRadius: 5, marginTop: -35 },
                          ]}
                          onPress={() => {
                            const selectedDayjs = dayjs(popoverDate);
                            setShowDatePicker(false);
                            setSelectedDate(selectedDayjs);
                            onFilterChanged({
                              date: selectedDayjs.format(
                                constants.DBDATE_FORMAT,
                              ),
                              time: DateHelper.getToNextSlot(
                                selectedDayjs,
                              ).format(constants.DBTIME_FORMAT),
                            });
                          }}
                        >
                          OK
                        </Button>
                      </View>
                    </Popover>
                  </View>
                  <View
                    style={[
                      styles.secondRowDivider,
                      { backgroundColor: theme['text-basic-color'] },
                    ]}
                  />
                  <View style={themeStyle.flex1}>
                    {/* on mobile, it uses action sheet */}
                    {/* on web, it uses the popover */}
                    <Popover
                      visible={showGuestWeb}
                      anchor={() => (
                        <Button onPress={_changePartySize} plain>
                          <ListItem
                            contentContainerStyle={[
                              themeStyle.pageVerticalSpacingMedium,
                              themeStyle.pageHorizontalSpacingSmall,
                            ]}
                            leftIcon={{ name: 'people-outline' }}
                            rightIcon={{ name: 'chevron-down' }}
                            spacing={5}
                            title={
                              <Text numberOfLines={1}>
                                {size} {pluralize(size, 'guest')}
                              </Text>
                            }
                          />
                        </Button>
                      )}
                      placement="bottom"
                      onBackdropPress={() => setShowGuestWeb(false)}
                      style={themeStyle.spacingTopSmall}
                      fullWidth
                    >
                      <View
                        style={[
                          themeStyle.pageVerticalSpacingSmall,
                          themeStyle.pageHorizontalSpacingSmall,
                          themeStyle.shadow,
                        ]}
                      >
                        <Text
                          status="info"
                          style={themeStyle.spacingBottomSmall}
                        >
                          Party Size
                        </Text>
                        <View style={themeStyle.flexDirectionRowCenter}>
                          <View
                            style={[
                              themeStyle.flex1,
                              themeStyle.flexDirectionRowSpaceBetween,
                            ]}
                          >
                            <Button
                              hitSlop={10}
                              onPress={() => setPopoverSize((prev) => prev - 1)}
                              plain
                            >
                              <Icon
                                name="minus"
                                fill={theme['color-info-500']}
                                style={styles.guestPopoverIcon}
                              />
                            </Button>
                            <Text category="s1">{popoverSize}</Text>
                            <Button
                              hitSlop={10}
                              onPress={() => setPopoverSize((prev) => prev + 1)}
                              plain
                            >
                              <Icon
                                name="plus"
                                fill={theme['color-info-500']}
                                style={styles.guestPopoverIcon}
                              />
                            </Button>
                          </View>
                          <Button
                            status="info"
                            size="small"
                            style={[
                              { borderRadius: 5 },
                              themeStyle.spacingLeft,
                            ]}
                            onPress={() => {
                              setShowGuestWeb(false);
                              setSize(popoverSize);
                              onFilterChanged({ party_size: popoverSize });
                            }}
                          >
                            Ok
                          </Button>
                        </View>
                      </View>
                    </Popover>
                  </View>
                </View>
              </View>
            }
            textInputProps={
              {
                InputComp: StyledSearch,
                placeholder: 'Search',
                onRef: (r) => (inputRef.current = r),
              } as any
            }
            style={
              constants.isWeb
                ? { backgroundColor: 'transparent' }
                : ({
                    container: themeStyle.flex1,
                    textInputContainer: {
                      ...themeStyle.flex1,
                      ...themeStyle.flexDirectionRow,
                    },
                    listView: themeStyle.shadow,
                    row: {
                      ...themeStyle.pageBackground,
                      ...themeStyle.pageVerticalSpacingMedium,
                    },
                  } as any)
            }
            // mobile props
            // @ts-ignore: debounce props is only for mobile, on web its ignored
            debounce={0}
            fill={theme['color-info-500']}
            onDone={_onAddressSelect} // mobile callback when addresss is selected
            suppressDefaultStyles
            // web props
            iconFill={theme['color-info-500']}
            onSelected={_onAddressSelect} // web callback when addresss is selected
          />
        </View>
        {/* Search Result */}
      </SafeAreaView>

      {!constants.isWeb && (
        <DatePicker
          cancelText="Cancel"
          open={showDatePicker}
          value={selectedDate}
          onCancel={() => setShowDatePicker(false)}
          onConfirm={(selected) => {
            const selectedDayjs = dayjs(selected);
            setShowDatePicker(false);
            setSelectedDate(selected);
            onFilterChanged({
              date: selectedDayjs.format(constants.DBDATE_FORMAT),
              time: selectedDayjs.format(constants.DBTIME_FORMAT),
            });
          }}
        />
      )}
    </View>
  );
};

const topPos = Platform.select({
  android: constants.statusBarHeight,
  ios: globalSpacing / 2,
  web: globalSpacing,
});
const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    top: topPos,
    left: 0,
    right: 0,
    zIndex: 1,
  },
  firstRowRadiusAndBg: {
    ...themeStyle.shadow,
    ...themeStyle.pageBackground,
    borderRadius: 10,
  },
  button: {
    ...themeStyle.pageHorizontalSpacing,
    ...themeStyle.pageVerticalSpacingSmall,
  },
  buttonIcon: {
    width: 30,
    height: 30,
  },
  dateAndPartySizeContainer: {
    ...themeStyle.shadow,
    ...themeStyle.pageBackground,
    ...themeStyle.flexDirectionRowCenter,
    borderRadius: 25,
  },
  secondRowDivider: {
    height: '75%',
    width: 1,
  },
  guestPopoverIcon: {
    width: 20,
    height: 20,
  },
});

export default ReservationLocateOnMapHeader;
